import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../utils";
import { useTheme } from "../../contexts/ThemeContext";
import ModeDropdown from "../../components/ModeDropdown";

export default function ViolationsV2Dashboard() {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const CARD_STYLE = isDark
    ? {
        properties: "border-emerald-400/40 bg-emerald-500/10 text-emerald-100",
        violations: "border-indigo-400/40 bg-indigo-500/10 text-indigo-100",
        unsafe: "border-rose-400/40 bg-rose-500/10 text-rose-100",
        vacate: "border-amber-400/40 bg-amber-500/10 text-amber-100",
        balance: "border-sky-400/40 bg-sky-500/10 text-sky-100",
      }
    : {
        properties: "border-emerald-100 bg-emerald-50 text-emerald-700",
        violations: "border-indigo-100 bg-indigo-50 text-indigo-700",
        unsafe: "border-rose-100 bg-rose-50 text-rose-700",
        vacate: "border-amber-100 bg-amber-50 text-amber-700",
        balance: "border-sky-100 bg-sky-50 text-sky-700",
      };

  const StatCard = ({ label, value, style }) => (
    <div className={`relative overflow-hidden rounded-2xl p-4 border ${style}`}>
      <div className="absolute inset-0 opacity-40 bg-gradient-to-br from-white/40 via-transparent to-white/10" />
      <div className="relative">
        <div className="text-[11px] uppercase tracking-wide font-semibold">{label}</div>
        <div className={`mt-1 text-2xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>
          {summaryLoading ? "..." : value ?? 0}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api/violations-v2/dashboard-summary`);
        setSummary(res.data);
      } catch (err) {
        console.error("V2 summary fetch error", err);
        setError("Failed to load summary.");
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  }, []);

  return (
    <div
      className="p-3 md:p-4 lg:p-5 space-y-3"
      style={{
        "--control-height": "28px",
        "--control-font": "10px",
        "--control-pad-x": "10px",
        "--control-pad-y": "3px",
      }}
    >
      <div
        className={`relative overflow-hidden rounded-3xl border ${
          isDark
            ? "border-slate-800/80 bg-slate-900/75 shadow-[0_30px_90px_rgba(0,0,0,0.45)]"
            : "border-slate-200 bg-white/85 shadow-[0_20px_60px_rgba(15,23,42,0.10)]"
        }`}
      >
        <div
          className={`absolute inset-0 opacity-80 ${
            isDark
              ? "bg-gradient-to-r from-indigo-500/10 via-cyan-500/10 to-transparent"
              : "bg-gradient-to-r from-indigo-100/60 via-cyan-100/50 to-transparent"
          }`}
          aria-hidden
        />
        <div className="relative px-4 py-4 md:px-5 md:py-4 lg:px-6 lg:py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-500 flex items-center justify-center text-[15px] font-bold text-white shadow-lg shadow-indigo-500/25">
                V
              </div>
              <div className="leading-tight">
                <p className="page-kicker">Web Platform</p>
                <h1 className="page-title">Violations Dashboard</h1>
                <p className="page-subtitle">Operational dashboard for high-volume violations data.</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ModeDropdown className="h-8 px-3 rounded-full shadow-sm" />
              <div
                className={`flex items-center gap-2 rounded-full px-3 py-1 text-[11px] ${
                  isDark
                    ? "border border-slate-700 bg-slate-800/60 text-slate-300"
                    : "border border-slate-200 bg-slate-100 text-slate-700"
                }`}
              >
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden />
                <span className="font-semibold">Live</span>
                <span className={isDark ? "text-slate-400" : "text-slate-500"}>data</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        <StatCard
          label="Total Properties"
          value={summary?.total_properties}
          style={CARD_STYLE.properties}
        />
        <StatCard
          label="Total Violations"
          value={summary?.total_violations}
          style={CARD_STYLE.violations}
        />
        <StatCard
          label="Unsafe Buildings"
          value={summary?.unsafe_properties}
          style={CARD_STYLE.unsafe}
        />
        <StatCard
          label="Vacate Orders"
          value={summary?.vacate_properties}
          style={CARD_STYLE.vacate}
        />
        <StatCard
          label="High Balance"
          value={summary?.high_balance_properties}
          style={CARD_STYLE.balance}
        />
      </div>

      {error && (
        <div className="surface-card rounded-2xl p-3 text-sm text-rose-600">{error}</div>
      )}
    </div>
  );
}
